import React, { useCallback, useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import Snackbar, { SnackbarProps } from '@material-ui/core/Snackbar';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import { makeStyles, Theme } from '@material-ui/core/styles';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Dialog from '@material-ui/core/Dialog';
import {
  complete,
  getNotification,
  hideNotification,
  undo,
  undoableEventEmitter,
  useTranslate
} from 'react-admin';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';

import { SuccessIcon } from '../../../themes/icons/SuccessIcon';
import { ErrorIcon } from '../../../themes/icons/ErrorIcon';

export interface NotificationProps extends Omit<SnackbarProps, 'open'> {
  type?: string;
  autoHideDuration?: number;
  multiLine?: boolean;
}

const variantIcon = {
  warning: ErrorIcon, // in the future maybe WarningIcon
  error: ErrorIcon,
  info: SuccessIcon,
  customWarning: ErrorIcon,
  forkedWarning: ErrorIcon
};

const useStyles = makeStyles((theme: Theme) => ({
  success: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText
  },
  error: {
    backgroundColor: '#FFEBEE',
    border: '1px solid #D50000',
    boxSizing: 'border-box',
    borderRadius: '2px'
  },
  info: {
    backgroundColor: '#E8F5E9',
    border: '1px solid #00A645',
    boxSizing: 'border-box',
    borderRadius: '2px'
  },
  warning: {
    backgroundColor: '#FFEBEE',
    border: '1px solid #D50000',
    boxSizing: 'border-box',
    borderRadius: '2px'
  },
  forkedWarning: {
    backgroundColor: '#FFF4E5',
    border: '1px solid #FFA500',
    boxSizing: 'border-box',
    borderRadius: '2px'
  },
  customWarning: {
    backgroundColor: '#FFA500',
    border: '1px solid #FFA500',
    boxSizing: 'border-box',
    borderRadius: '2px'
  },
  undo: (props: NotificationProps) => ({
    color:
      props.type === 'success'
        ? theme.palette.success.contrastText
        : theme.palette.primary.light
  }),
  multiLine: {
    whiteSpace: 'pre-wrap'
  },
  icon: {
    fontSize: 20,
    marginRight: '20px'
  },
  iconVariant: {
    opacity: 0.9
  },
  errorMessage: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '10px',
    color: '#D50000'
  },
  infoMessage: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '10px',
    color: '#008437'
  },
  warningMessage: {
    display: 'flex',
    alignItems: 'center',
    marginRight: '10px',
    color: '#FFA500'
  },
  notificationContent: {
    display: 'flex'
  }
}));

const Notification = (props: NotificationProps) => {
  const {
    classes: classesOverride,
    type,
    className,
    autoHideDuration,
    multiLine,
    ...rest
  } = props;
  const [open, setOpen] = useState(false);
  const notification = useSelector(getNotification);
  const dispatch = useDispatch();
  const translate = useTranslate();
  const styles = useStyles({});
  let notificationType = (notification && notification.type) || type;
  const messageArgs = (notification && notification.messageArgs) || null;
  //only explicitly set New Admin custom warnings will be orange, otherwise red
  if (messageArgs && messageArgs.isNewAdminCustomWarning) {
    notificationType = 'customWarning';
  } else if (messageArgs && messageArgs.isForkedWarning) {
    notificationType = 'forkedWarning';
  }
  const Icon = variantIcon[notificationType];

  let messageStyle = styles.infoMessage;
  if (
    notificationType === 'customWarning' ||
    notificationType === 'forkedWarning'
  ) {
    messageStyle = styles.warningMessage;
  } else if (notificationType !== 'info') {
    messageStyle = styles.errorMessage;
  }

  useEffect(() => {
    setOpen(!!notification);
  }, [notification]);

  const handleRequestClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleExited = useCallback(() => {
    if (notification && notification.undoable) {
      dispatch(complete());
      undoableEventEmitter.emit('end', { isUndo: false });
    }
    dispatch(hideNotification());
  }, [dispatch, notification]);

  const handleUndo = useCallback(() => {
    dispatch(undo());
    undoableEventEmitter.emit('end', { isUndo: true });
  }, [dispatch]);

  if (!notification) return null;

  return (
    <Dialog open={open} BackdropProps={{ invisible: true }}>
      <Snackbar
        open={open}
        autoHideDuration={notification.autoHideDuration || autoHideDuration}
        disableWindowBlurListener={notification.undoable}
        TransitionProps={{ onExited: handleExited }}
        onClose={handleRequestClose}
        ContentProps={{
          className: classNames(styles[notification.type || type], className, {
            [styles['multiLine']]: notification.multiLine || multiLine
          })
        }}
        action={
          notification.undoable ? (
            <Button
              color="primary"
              className={styles.undo}
              size="small"
              onClick={handleUndo}
            >
              {translate('ra.action.undo')}
            </Button>
          ) : null
        }
        {...rest}
      >
        <SnackbarContent
          className={clsx(styles[notificationType], className)}
          aria-describedby="client-snackbar"
          message={
            <div className={styles.notificationContent}>
              <span id="client-snackbar" className={messageStyle}>
                <Icon className={clsx(styles.icon, styles.iconVariant)} />
              </span>
              <span>
                {notification &&
                  notification.message &&
                  translate(notification.message, notification.messageArgs)}
              </span>
            </div>
          }
        />
      </Snackbar>
    </Dialog>
  );
};

Notification.propTypes = {
  type: PropTypes.string,
  autoHideDuration: PropTypes.number,
  multiLine: PropTypes.bool
};

Notification.defaultProps = {
  type: 'info',
  autoHideDuration: 10000,
  multiLine: false
};

export default Notification;
